import React, { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import {
	Stack,
	Box,
	Snackbar,
	Typography,
	Grid,
	Divider,
	useTheme,
} from "@mui/material";
import colorsCustom from "../designTokens/colorsCustom";
import { useTranslation } from "react-i18next";
import { IconThreads } from "../assets/icon/metric/IconThreads";
import { IconShare } from "@tabler/icons-react";
import { useLocation, useNavigate } from "react-router-dom";
import { ButtonBasic } from "./button/ButtonBasic";
import { useThreadsAuth } from "../hooks/useThreadsAuth";
import { ThreadTitle } from "./typography/ThreadTitle";
import { ThreadParagraph } from "./typography/ThreadParagraph";
import { cleanupMediaUrls, processThreadMediaWithProxy } from "../utils/mediaDownloader";
import { getConversationPure } from "../utils/pureDataFetch";
import moment from "moment";
import layout from "../designTokens/layout";
import { CardCollectionBook } from "./Card/CardCollectionBook";
import { getFirstSentence as getFirstSentenceUtil } from "../utils/textUtils";
import { CardContainerRetro } from "./Container/CardContainerRetro";
import { getAllCollection } from "../utils/pureDataFetch";
import { redirectToCollectionLogin } from "../utils/collectionLogin";

const CollectionThreadContent = ({
	selectedThread,
	isMobile,
	setSelectedThread,
	threadList,
	collectionID,
	isVisible,
	setIsVisible,
	collection,
  isMe,
  isLogin,
}) => {
  const { login: threadsLogin } = useThreadsAuth();
	const { t } = useTranslation();
	const location = useLocation();
	const theme = useTheme();
	const navigate = useNavigate();
	// 현재 선택 인덱스는 외부 상태(selectedThread)로 관리하므로 내부 인덱스 상태 불필요

	const [urlSentence, setUrlSentence] = useState("");
	const [showSnackbar, setShowSnackbar] = useState(false);
	const [threadComments, setThreadComments] = useState([]);
	const [loadingComments, setLoadingComments] = useState(false);
	const [commentsCache, setCommentsCache] = useState({});
	const [otherCollections, setOtherCollections] = useState([]);
  const handleLoginRequest = useCallback(() => {
    redirectToCollectionLogin(threadsLogin);
  }, [threadsLogin]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const sentence = params.get("sentence");
		if (sentence) {
			setUrlSentence(sentence);
		}
	}, [location.search]);

	// 컬렉션이 변경될 때 캐시 초기화
	useEffect(() => {
		setCommentsCache({});
		setThreadComments([]);
	}, [collectionID]);

	// 동일 사용자(other user)의 다른 컬렉션 로드 (공개 컬렉션만, 본인이면 비공개 포함)
	useEffect(() => {
		if (!collection) return;
		getAllCollection()
			.then((all) => {
				const filtered = all
					.filter((c) => {
						const isSameUser = c.userID === collection.userID;
						const isNotCurrent = c.id !== collection.id;
						const isVisible = isMe ? true : c.isPublic ?? true;
						const hasTitle = c.title && c.title.length > 0;
						return isSameUser && isNotCurrent && isVisible && hasTitle;
					})
					.sort((a, b) => {
						const aDate = new Date(a.lastUpdated || a.createdAt);
						const bDate = new Date(b.lastUpdated || b.createdAt);
						return bDate - aDate;
					});
				setOtherCollections(filtered);
			})
			.catch((e) => console.error("Error loading other collections:", e));
	}, [collection, isMe]);

	// 연속적으로 작성된 댓글 체인을 찾는 함수
	const findContinuousCommentChain = useCallback(
		(conversation, authorUsername, threadTimestamp) => {
			// 1. 작성자의 username으로 모든 댓글/대댓글 찾기
			const userComments = conversation
				.filter((comment) => comment.username === authorUsername)
				.sort((a, b) => moment(a.timestamp).diff(moment(b.timestamp)));

			if (userComments.length === 0) return [];

			const threadTime = moment(threadTimestamp);

			// 2. 본문 작성 시간으로부터 5분 이내에 작성된 댓글들만 필터링
			const commentsWithinTimeWindow = userComments.filter((comment) => {
				const commentTime = moment(comment.timestamp);
				const timeDiffFromThread = Math.abs(
					commentTime.diff(threadTime, "minutes")
				);
				return timeDiffFromThread <= 5;
			});

			// 3. 체인이 본문을 이어쓰기 위한 것인지 판단
			if (commentsWithinTimeWindow.length === 0) return [];

			// 5분 이내에 작성된 댓글이 있으면 모두 본문 이어쓰기로 간주
			return commentsWithinTimeWindow;
		},
		[]
	);

	// 댓글 체인의 구조를 분석하는 함수
	const analyzeCommentStructure = useCallback((comments) => {
		const structure = [];

		for (const comment of comments) {
			// replied_to 필드를 통해 대댓글 관계 파악
			const isReply =
				comment.replied_to && comment.replied_to !== comment.root_post;
			const parentId =
				typeof comment.replied_to === "object"
					? comment.replied_to.id
					: comment.replied_to;

			structure.push({
				...comment,
				isReply,
				parentId,
				level: isReply ? 1 : 0, // 단순화: 대댓글은 레벨 1, 직접 댓글은 레벨 0
			});
		}

		return structure;
	}, []);

	// 선택된 스레드의 연속 댓글을 가져오는 함수
	const fetchThreadComments = useCallback(
		async (threadId, threadTimestamp, threadUsername) => {
			if (!isLogin) {
				setThreadComments([]);
				return;
			}
			if (!threadId || !threadTimestamp || !threadUsername) return;

			// 캐시에서 먼저 확인
			if (commentsCache[threadId]) {
				console.log("📦 Loading comments from cache for thread:", threadId);
				setThreadComments(commentsCache[threadId]);
				return;
			}

			setLoadingComments(true);
      try {
				const conversation = await getConversationPure(threadId);

				// 스레드 작성자의 username으로 연속 댓글 찾기
				const continuousChain = findContinuousCommentChain(
					conversation,
					threadUsername,
					threadTimestamp
				);

				// 댓글 구조 분석
        const structuredComments = analyzeCommentStructure(continuousChain);

        // 댓글에 미디어 정보 가공 및 프록시 적용
        const withMedia = structuredComments.map((comment) => {
          const processed = processCommentWithMedia(comment);
          return processed.hasMedia
            ? processThreadMediaWithProxy(processed)
            : processed;
        });

				// 캐시에 저장
				setCommentsCache((prev) => ({
					...prev,
          [threadId]: withMedia,
				}));

        setThreadComments(withMedia);
			} catch (error) {
				console.error("Failed to fetch thread comments:", error);
				setThreadComments([]);
			} finally {
				setLoadingComments(false);
			}
		},
		[
			commentsCache,
			findContinuousCommentChain,
			analyzeCommentStructure,
			isLogin,
		]
	);

	// 선택된 스레드가 변경될 때마다 댓글 가져오기
	useEffect(() => {
		if (!isLogin) {
			setThreadComments([]);
			return;
		}

		if (selectedThread?.id) {
			fetchThreadComments(
				selectedThread.id,
				selectedThread.timestamp,
				selectedThread.username
			);
		}
	}, [
		isLogin,
		selectedThread?.id,
		selectedThread?.timestamp,
		selectedThread?.username,
		fetchThreadComments,
	]);

	// 컴포넌트 언마운트 시 메모리 정리
useEffect(() => {
    return () => {
      if (selectedThread?.mediaUrls) {
        cleanupMediaUrls(selectedThread.mediaUrls);
      }
      if (Array.isArray(threadComments) && threadComments.length > 0) {
        threadComments.forEach((c) => {
          if (c?.mediaUrls) cleanupMediaUrls(c.mediaUrls);
        });
      }
    };
  }, [selectedThread, threadComments]);

	// prev/next 내비게이션은 현재 UI에서 미사용이므로 제거

	// noop

    const getFirstSentence = useCallback((text) => getFirstSentenceUtil(text), []);

    const getBodyText = useCallback((text) => {
		// 첫 번째 문장을 제거한 나머지 텍스트 반환
		const firstSentence = getFirstSentence(text);
		let remainingText = text.replace(firstSentence, "").trim();

		// 첫 번째 문자가 마침표나 개행인 경우 제거
		if (remainingText.startsWith(".") || remainingText.startsWith("\n")) {
			remainingText = remainingText.substring(1).trim();
		}

		return remainingText;
    }, [getFirstSentence]);

  const fullBodyText = useMemo(() => {
		if (!selectedThread?.text) return "";
		return getBodyText(selectedThread.text);
  }, [selectedThread?.text, getBodyText]);

	// Threads 웹 스타일: 고정 높이 + 가변 너비 가로 스크롤 캐러셀
	const MediaCarousel = React.memo(({ thread, isMobile }) => {
		const fixedHeight = isMobile ? 240 : 320;
		const gap = 1; // MUI spacing unit (8px)
		const containerRef = React.useRef(null);
		const [isDragging, setIsDragging] = useState(false);
		const dragStartXRef = React.useRef(0);
		const scrollStartRef = React.useRef(0);
		const [isContainerLoading, setIsContainerLoading] = useState(true);
		const threadLoadedMapRef = React.useRef(new Map());
		const widthCacheRef = React.useRef(new Map()); // key -> width(px)

		useEffect(() => {
			if (!thread || !thread.id) return;
			const alreadyLoaded = threadLoadedMapRef.current.get(thread.id);
			setIsContainerLoading(!alreadyLoaded);
		}, [thread, thread?.id]);

		const validMedia = React.useMemo(
			() => (thread.mediaUrls || []).filter((m) => m && m.url),
			[thread.mediaUrls]
		);

		if (
			!thread.hasMedia ||
			!thread.mediaUrls ||
			thread.mediaUrls.length === 0 ||
			validMedia.length === 0
		) {
			return null;
		}

		// 동일 thread.id 재렌더 시 placeholder 재등장 방지
		// (위로 이동)

		const handlePointerDown = (e) => {
			const el = containerRef.current;
			if (!el) return;
			setIsDragging(true);
			dragStartXRef.current =
				e.clientX || (e.touches && e.touches[0]?.clientX) || 0;
			scrollStartRef.current = el.scrollLeft;
			// prevent image drag
			e.preventDefault();
		};

		const handlePointerMove = (e) => {
			if (!isDragging) return;
			const el = containerRef.current;
			if (!el) return;
			const clientX = e.clientX || (e.touches && e.touches[0]?.clientX) || 0;
			const delta = dragStartXRef.current - clientX;
			el.scrollLeft = scrollStartRef.current + delta;
		};

		const handlePointerUp = () => {
			setIsDragging(false);
		};

		const handleAnyLoaded = () => {
			// 첫 미디어 로드 시 플레이스홀더 제거
			if (!threadLoadedMapRef.current.get(thread.id)) {
				threadLoadedMapRef.current.set(thread.id, true);
				setIsContainerLoading(false);
			}
		};

		return (
			<Box
				sx={{
					mt: 3,
					mb: 2,
					width: "100%",
					height: fixedHeight,
					overflow: "clip",
					minWidth: 0,
					maxWidth: "100%",
					display: "block",
					position: "relative",
					contain: "layout paint size",
				}}
			>
				<Box
					sx={{
						position: "relative",
						height: fixedHeight,
					}}
				>
					{/* 컨테이너 전체 로딩 그라데이션 */}
					{isContainerLoading && (
						<Box
							sx={{
								position: "absolute",
								inset: 0,
								borderRadius: "8px",
								overflow: "hidden",
								background:
									"linear-gradient(90deg, rgba(0,0,0,0.04) 25%, rgba(0,0,0,0.08) 37%, rgba(0,0,0,0.04) 63%)",
								backgroundSize: "200% 100%",
								animation: "mtiShimmer 1.2s linear infinite",
								pointerEvents: "none",
								"@keyframes mtiShimmer": {
									"0%": { backgroundPosition: "-200% 0" },
									"100%": { backgroundPosition: "200% 0" },
								},
							}}
						/>
					)}

					<Box
						ref={containerRef}
						onMouseDown={handlePointerDown}
						onMouseMove={handlePointerMove}
						onMouseUp={handlePointerUp}
						onMouseLeave={handlePointerUp}
						onTouchStart={handlePointerDown}
						onTouchMove={handlePointerMove}
						onTouchEnd={handlePointerUp}
						sx={{
							display: "flex",
							overflowX: "auto",
							scrollSnapType: "x mandatory",
							WebkitOverflowScrolling: "touch",
							gap,
							borderRadius: 1,
							msOverflowStyle: "none",
							scrollbarWidth: "none",
							"&::-webkit-scrollbar": { display: "none" },
							height: fixedHeight,
							minWidth: 0,
							maxWidth: "100%",
							width: "100%",
							cursor: isDragging ? "grabbing" : "grab",
						}}
					>
						{validMedia.map((media, idx) => (
							<CarouselItem
								key={`${media.id || media.originalUrl || media.url || idx}`}
								media={media}
								fixedHeight={fixedHeight}
								onLoaded={handleAnyLoaded}
								widthCacheRef={widthCacheRef}
							/>
						))}
					</Box>
				</Box>
			</Box>
		);
	});

	const CarouselItem = ({ media, fixedHeight, onLoaded, widthCacheRef }) => {
		const cacheKey = media.id || media.originalUrl || media.url || "";
		const [itemWidth, setItemWidth] = useState(() => {
			const cached = cacheKey ? widthCacheRef.current.get(cacheKey) : null;
			return cached || fixedHeight; // placeholder 정사각
		});

		const handleImageLoad = (e) => {
			const img = e.currentTarget;
			if (img && img.naturalWidth && img.naturalHeight) {
				const ar = img.naturalWidth / img.naturalHeight;
				const w = Math.max(120, Math.round(fixedHeight * ar));
				setItemWidth(w);
				if (cacheKey) widthCacheRef.current.set(cacheKey, w);
				onLoaded && onLoaded();
			}
		};

		const handleVideoMeta = (e) => {
			const v = e.currentTarget;
			if (v && v.videoWidth && v.videoHeight) {
				const ar = v.videoWidth / v.videoHeight;
				const w = Math.max(160, Math.round(fixedHeight * ar));
				setItemWidth(w);
				if (cacheKey) widthCacheRef.current.set(cacheKey, w);
				onLoaded && onLoaded();
			}
		};

		return (
			<Box
				sx={{
					position: "relative",
					height: fixedHeight,
					width: itemWidth,
					borderRadius: "8px",
					overflow: "hidden",
					// 컨테이너가 전체 플레이스홀더를 갖기 때문에 아이템 배경은 사용하지 않음
					scrollSnapAlign: "start",
					flex: "0 0 auto",
					cursor: media.type === "VIDEO" ? "default" : "pointer",
				}}
				onClick={() => {
					if (media.type !== "VIDEO") {
						const urlToOpen = media.isBlob
							? media.url
							: media.originalUrl || media.url;
						window.open(urlToOpen, "_blank", "noopener,noreferrer");
					}
				}}
			>
				{media.type === "VIDEO" ? (
					<video
						src={media.url}
						playsInline
						muted
						loop
						autoPlay
						onLoadedMetadata={handleVideoMeta}
						style={{
							position: "absolute",
							inset: 0,
							width: "100%",
							height: "100%",
							objectFit: "cover",
						}}
					/>
				) : (
					<Box
						component="img"
						src={media.url}
						alt={media.alt || ""}
						onLoad={handleImageLoad}
						loading="eager"
						crossOrigin="anonymous"
						referrerPolicy="no-referrer"
						sx={{
							position: "absolute",
							inset: 0,
							width: "100%",
							height: "100%",
							objectFit: "cover",
							display: "block",
						}}
					/>
				)}
			</Box>
		);
	};

	// 연속 댓글을 본문과 완전 동일한 UI로 표시하는 컴포넌트
	const ContinuousCommentsDisplay = () => {
		if (!isLogin) {
			return null;
		}

		if (loadingComments) {
			return null; // 로딩 중에는 아무것도 표시하지 않음
		}

		if (threadComments.length === 0) {
			return null;
		}

		return (
			<Stack spacing={0}>
				{/* 본문과 첫 번째 댓글 사이 구분선 */}
				<Box
					sx={{
						width: "100%",
						height: "0.5px",
						backgroundColor: colorsCustom.ui.dark.subtle,
						my: 3, // 위아래 여백
					}}
				/>

        {threadComments.map((comment, index) => (
					<React.Fragment key={comment.id || index}>
						{/* 댓글과 댓글 사이 구분선 (첫 번째 댓글이 아닌 경우) */}
						{index > 0 && (
							<Box
								sx={{
									width: "100%",
									height: "0.5px",
									backgroundColor: colorsCustom.ui.dark.subtle,
									my: 3, // 위아래 여백
								}}
							/>
						)}

						{/* 댓글 내용을 본문과 동일한 스타일로 표시 */}
						<ThreadParagraph
							isMobile={isMobile}
							text={comment.text}
							highlightText=""
						/>
            {isLogin && comment.hasMedia && (
              <MediaCarousel thread={comment} isMobile={isMobile} />
            )}
					</React.Fragment>
				))}
			</Stack>
		);
	};

  // 댓글 객체의 미디어 정보를 본문과 동일한 구조로 변환
  const processCommentWithMedia = (comment) => {
    let childrenArray = [];
    if (
      comment.children &&
      comment.children.data &&
      Array.isArray(comment.children.data)
    ) {
      childrenArray = comment.children.data;
    }

    const hasMedia = !!(comment.media_url || childrenArray.length > 0);
    const mediaUrls = [];

    if (childrenArray.length > 0) {
      childrenArray.forEach((child) => {
        if (child.media_url) {
          mediaUrls.push({
            url: child.media_url,
            type: child.media_type,
            alt: child.alt_text || "",
          });
        }
      });
    } else if (comment.media_url) {
      mediaUrls.push({
        url: comment.media_url,
        type: comment.media_type,
        alt: comment.alt_text || "",
      });
    }

    return {
      ...comment,
      hasMedia,
      mediaType: comment.media_type,
      mediaUrls,
      isCarousel: !!(childrenArray.length > 1),
    };
  };

	return (
		<>
			<Box
				pb={isMobile ? 12 : 4}
				sx={{
					width: "100%",
					flex: 1, // flex로 나머지 공간 차지
					display: "flex",
					justifyContent: "flex-start", // 왼쪽 정렬
					padding: isMobile ? "0" : "0 24px 0 0", // 모바일 패딩 제거
					overflowX: "clip",
					position: "relative",
					height: "100%",
				}}
			>
				<Stack
					spacing={isMobile ? 3 : 4}
					sx={{
						width: isMobile
							? layout.content.maxWidth.mobile
							: layout.content.maxWidth.desktop,
						maxWidth: "100%", // 반응형 대응
						minWidth: 0,
						height: "100%",
						// 모든 자식 요소가 부모 너비를 넘지 않도록 강제
						"& > *": {
							maxWidth: "100%",
							boxSizing: "border-box",
						},
					}}
				>
					<ThreadTitle isMobile={isMobile}>
						{getFirstSentence(selectedThread.text)}
					</ThreadTitle>
					{fullBodyText && (
						<ThreadParagraph
							isMobile={isMobile}
							text={fullBodyText}
							highlightText={isLogin ? urlSentence : ""}
						/>
					)}

					{isLogin && (() => {
						const extractTextFromAttachment = (att) => {
							if (!att) return '';
							if (typeof att === 'string') return att;
							if (Array.isArray(att)) {
								return att.map(extractTextFromAttachment).filter(Boolean).join('\n');
							}
							if (typeof att === 'object') {
								return (
									att.plaintext || att.plain_text || att.text || (typeof att.content === 'string' ? att.content : '') || ''
								);
							}
							return String(att);
						};
						const taText = extractTextFromAttachment(selectedThread?.text_attachment);
						if (!taText) return null;
						return (
							<Box
								sx={{
									mt: 2,
									p: isMobile ? 2 : 2.5,
									borderRadius: 2,
									border: `1px solid ${colorsCustom.ui.dark.subtle}`,
									backgroundColor: 'rgba(0,0,0,0.02)'
								}}
							>
								<Typography variant={isMobile ? "body2" : "body1"} sx={{ whiteSpace: 'pre-wrap' }}>
									{taText}
								</Typography>
								{selectedThread?.link_attachment_url && (
									<Box sx={{ mt: 1.5 }}>
										<ButtonBasic
											actionType="secondary"
											variant="outlined"
											size={isMobile ? "sm" : "md"}
											widthType="manual"
											href={selectedThread.link_attachment_url}
											component="a"
											target="_blank"
											rel="noreferrer"
										>
											링크 열기
										</ButtonBasic>
									</Box>
								)}
							</Box>
						);
					})()}

					{isLogin && (
						<>
							{/* 미디어 표시: 고정 높이 + 가변 너비 가로 스크롤 */}
							<MediaCarousel thread={selectedThread} isMobile={isMobile} />

							{/* 연속 댓글을 본문 스타일로 표시 */}
							<ContinuousCommentsDisplay />
						</>
					)}

					{!isLogin && (
						<Box
							sx={{
								width: "100%",
								mt: 3,
								p: isMobile ? 2.5 : 3,
								borderRadius: 2,
								border: `1px solid ${colorsCustom.ui.dark.subtle}`,
								backgroundColor: "rgba(0, 0, 0, 0.02)",
								textAlign: "center",
							}}
						>
							<Typography
								variant="body1"
								sx={{
									mb: 2,
									color: "text.secondary",
									fontWeight: 500,
									wordBreak: "keep-all",
								}}
							>
								로그인하고 이어지는 본문, 이미지, 댓글을 모두 만나보세요
							</Typography>
							<ButtonBasic
								actionType="primary"
								variant="contained"
								size="md"
								widthType="manual"
								width={isMobile ? "100%" : "220px"}
								onClick={handleLoginRequest}
							>
								로그인 후 전체 보기
							</ButtonBasic>
						</Box>
					)}

					{/* 동일 사용자의 다른 컬렉션 섹션 - 콘텐츠 최하단 */}
					{!isMobile && (
						<Box
							sx={{
								width: "100%",
								display: "flex",
								justifyContent: "space-between",
								marginTop: 10,
								gap: 1,
							}}
						>
							<ButtonBasic
								actionType="primary"
								variant="contained"
								size="xl"
								widthType="manual"
								width="50%"
								leftSection={<IconThreads />}
								onClick={() =>
									window.open(
										selectedThread.permalink,
										"_blank",
										"noopener,noreferrer"
									)
								}
							>
								{t("myCollection.viewFullThread")}
							</ButtonBasic>
							<ButtonBasic
								actionType="primary"
								variant="outlined"
								size="xl"
								widthType="manual"
								width="50%"
								leftSection={<IconShare />}
								onClick={() => {
									navigator.clipboard.writeText(
										`https://mythreadsinsight.com/collection/${collectionID}`
									);
									setShowSnackbar(true);
								}}
							>
								{t("myCollection.share")}
							</ButtonBasic>
						</Box>
					)}
					{isMobile &&
						createPortal(
							<Box
								sx={{
									width: "100%",
									marginTop: 0,
									padding: "16px",
									position: "fixed",
									bottom: 0,
									left: 0,
									right: 0,
									backgroundColor: theme.palette.background.default,
									borderTop: `1px solid ${colorsCustom.ui.dark.subtle}`,
									zIndex: 2147483647, // 최상위 보장
									pointerEvents: "auto",
								}}
							>
								<Box sx={{ display: "flex", gap: 1 }}>
									<Box sx={{ flex: "3" }}>
										<ButtonBasic
											actionType="secondary"
											variant="outlined"
											size="sm"
											width="100%"
											widthType="manual"
											onClick={() => setIsVisible(!isVisible)}
										>
											목차
										</ButtonBasic>
									</Box>
									<Box sx={{ flex: "4.5" }}>
										<ButtonBasic
											actionType="primary"
											variant="outlined"
											size="sm"
											width="100%"
											widthType="manual"
											leftSection={<IconThreads size={16} />}
											component="a"
											target="_blank"
											rel="noreferrer"
											href={selectedThread.permalink}
										>
											스레드
										</ButtonBasic>
									</Box>
									<Box sx={{ flex: "4.5" }}>
										<ButtonBasic
											actionType="primary"
											variant="contained"
											size="sm"
											width="100%"
											widthType="manual"
											leftSection={<IconShare size={16} />}
											onClick={() => {
												navigator.clipboard.writeText(
													`https://mythreadsinsight.com/collection/${collectionID}`
												);
												setShowSnackbar(true);
											}}
										>
											공유
										</ButtonBasic>
									</Box>
								</Box>
							</Box>,
							document.body
						)}
					{otherCollections && otherCollections.length > 0 && (
						<Box
							sx={{
								width: "100%",
								pt: isMobile ? 4 : 8,
								position: "relative",
								zIndex: 0,
							}}
						>
							<Divider sx={{ my: isMobile ? 2 : 4 }} />
						</Box>
					)}

					{/* 최하단: 동일 사용자 컬렉션 + 커뮤니티 CTA */}
					{otherCollections && otherCollections.length > 0 && (
						<Stack
							spacing={2}
							sx={{
								width: "100%",
								pt: isMobile ? 4 : 8,
								position: "relative",
								zIndex: 0,
							}}
						>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
								}}
							>
								<Typography
									variant={isMobile ? "h6" : "h5"}
									sx={{ fontWeight: 800 }}
								>
									{t("collectionPage.otherCollectionsTitle", {
										name: collection?.name || collection?.username || "",
									})}
								</Typography>
							</Box>
							<Box
								width="100%"
								overflow="hidden"
								sx={{ position: "relative", zIndex: 0 }}
							>
								<Grid container spacing={1} disableEqualOverflow>
									{otherCollections.map((c) => (
										<Grid key={c.id} size={{ xs: 12, sm: 6 }}>
											<CardCollectionBook
												collection={c}
												isMobile={isMobile}
												isMe={false}
												size="small"
												categoryLabels={
													Array.isArray(c.categoryLabels)
														? c.categoryLabels
														: []
												}
											/>
										</Grid>
									))}
									{/* 모든 컬렉션 보기 카드 (small 스타일, 동일 컨테이너, 색 반전) - 마지막 배치 */}
									<Grid size={{ xs: 12, sm: 6 }}>
										<CardContainerRetro
											w={"100%"}
											rd={8}
											bg={theme.palette.text.primary}
											borderType={"full"}
										>
											<Box
												onClick={() => navigate("/all-collection")}
												sx={{ cursor: "pointer", width: "100%", height: 160 }}
											>
												<Stack
													spacing={1.5}
													sx={{
														p: isMobile ? 2 : 3,
														height: "100%",
														boxSizing: "border-box",
														justifyContent: "center",
													}}
												>
													<Typography
														variant={isMobile ? "h6" : "h6"}
														sx={{
															fontWeight: 800,
															textAlign: "center",
															color: theme.palette.background.paper,
														}}
													>
														{t("collectionPage.viewAllCollections")}
													</Typography>
												</Stack>
											</Box>
										</CardContainerRetro>
									</Grid>
								</Grid>
							</Box>
						</Stack>
					)}
				</Stack>
			</Box>
			<Snackbar
				open={showSnackbar}
				autoHideDuration={3000}
				onClose={() => setShowSnackbar(false)}
				message={`https://mythreadsinsight.com/collection/${collectionID}`}
			/>
		</>
	);
};

export default CollectionThreadContent;
